import { formatName } from '@leland-dev/leland-ui-library';
import { type GetServerSideProps, type GetServerSidePropsResult } from 'next';
import { type FC } from 'react';

import { type SkuTier } from '../../../__generated-gql-types__/globalTypes';
import PackagePage from '../../../components/package/PackagePage';
import {
  ApplicantPlanPageOfferingPackageBySlugDocument,
  type ApplicantPlanPageOfferingPackageBySlugQuery,
  type ApplicantPlanPageOfferingPackageBySlugQueryVariables,
  type ApplicantPlanPageOfferingPackageFragment,
} from '../../../components/package/__generated-gql-types__/[packageSlug].generated';
import NotFoundPage from '../../../components/page/NotFoundPage';
import Page from '../../../components/page/Page';
import PageContent, {
  getStaticPageContentQuery,
  type PageContentDataProps,
} from '../../../components/page/PageContent';
import { addApolloState, initializeApollo } from '../../../lib/apolloClient';
import {
  CACHE_CONTROL,
  SUBSCRIBER_DUMMY_COACH_URN,
} from '../../../utils/constants';
import { constructServerSideRenderingContext } from '../../../utils/requests';

interface ApplicantPlanPageProps {
  offeringPackage: Possible<ApplicantPlanPageOfferingPackageFragment>;
  pageContentProps: PageContentDataProps;
}

const ApplicantPlanPage: FC<ApplicantPlanPageProps> = ({
  offeringPackage,
  pageContentProps,
}) => {
  const offeringPackageToUse = offeringPackage;

  if (!offeringPackageToUse) {
    return (
      <Page title={`Package Not Found`}>
        <PageContent initialData={pageContentProps}>
          <NotFoundPage entity="package" />
        </PageContent>
      </Page>
    );
  }

  const titleFragments = [
    `${offeringPackageToUse.name} by ${formatName(
      offeringPackageToUse.coach.user,
    )}`,
  ];
  if (offeringPackageToUse.category) {
    titleFragments.push(`${offeringPackageToUse.category.name} Package`);
  }

  return (
    <Page
      title={titleFragments.join(' | ')}
      imageUrl={offeringPackageToUse.imageLink ?? undefined}
      noIndex
    >
      <PageContent initialData={pageContentProps}>
        <PackagePage offeringPackage={offeringPackageToUse} />
      </PageContent>
    </Page>
  );
};

const NOT_FOUND_PROPS: GetServerSidePropsResult<ApplicantPlanPageProps> = {
  notFound: true,
};

export const getServerSideProps: GetServerSideProps<
  ApplicantPlanPageProps,
  { categorySlug: string; packageSlug: string }
> = async ({ params, res, req, resolvedUrl, query }) => {
  res.setHeader('Cache-Control', CACHE_CONTROL);

  if (!params) {
    return NOT_FOUND_PROPS;
  }
  const { categorySlug, packageSlug } = params;

  const apolloClient = initializeApollo();

  const { data, errors } = await apolloClient.query<
    Possible<ApplicantPlanPageOfferingPackageBySlugQuery>,
    ApplicantPlanPageOfferingPackageBySlugQueryVariables
  >({
    query: ApplicantPlanPageOfferingPackageBySlugDocument,
    variables: {
      categorySlugOrUrn: categorySlug,
      slug: packageSlug,
      coach: SUBSCRIBER_DUMMY_COACH_URN,
      visibleTierOnly: !query.coachTier,
      tier: query.coachTier as Optional<SkuTier>,
    },
    context: constructServerSideRenderingContext(req, resolvedUrl),
  });

  if (!data?.offeringPackageBySlug) {
    if (errors && !errors?.some((e) => e.extensions?.statusCode === 404)) {
      // If not 404, throw to explicitly treat as 5xx
      console.warn(errors);
      throw new Error('Failed to load package data');
    }

    // don't return NOT_FOUND for 404 since there are different rules depending on caller
    // i.e. coach/ops can view a non-published package via this page, but customers cannot
  }

  const pageContentProps = await getStaticPageContentQuery();

  return addApolloState(apolloClient, {
    props: {
      offeringPackage: data?.offeringPackageBySlug,
      pageContentProps,
    },
  });
};

export default ApplicantPlanPage;
